import "zod/compile";
import { z } from "zod";

/**
 * Text templates for Report components: a component's title, description,
 * tooltip, notes, KPI value, Text markdown and Table cells can show values
 * from the dataset it is bound to, e.g.
 *
 *   "Of the {tasks_attempted} tasks the agent worked on."
 *   "{median_days} {median_days, plural, one {day} other {days}}"
 *   "{check_count - dated_checks} without a date."
 *
 * A placeholder is a value name (a column of the dataset's totals row, a row
 * field such as `check.count` in a Table cell, or `rows` / `total` for the
 * rows shown and in the whole source), optionally summed or subtracted with
 * others, optionally choosing between two words by plural form. The canvas
 * formats each value by its column's format. Kept tiny on purpose: no
 * nesting, no logic beyond + and −.
 */

export interface TextTerm {
  sign: 1 | -1;
  name: string;
}

export type TextPart =
  | { kind: "text"; text: string }
  | { kind: "value"; terms: TextTerm[] }
  | { kind: "plural"; terms: TextTerm[]; one: string; other: string };

export type TextTemplate = TextPart[];

export type TextTemplateParse =
  { ok: true; template: TextTemplate } | { ok: false; error: string };

const NAME = /^[A-Za-z_][A-Za-z0-9_.]*$/;

function parseTerms(source: string): TextTerm[] | string {
  const tokens = source
    .split(/([+-])/)
    .map((token) => token.trim())
    .filter((token) => token.length > 0);
  const terms: TextTerm[] = [];
  let sign: 1 | -1 = 1;
  let expectName = true;
  for (const token of tokens) {
    if (token === "+" || token === "-") {
      if (expectName) return `Unexpected "${token}" in "{${source}}"`;
      sign = token === "-" ? -1 : 1;
      expectName = true;
      continue;
    }
    if (!expectName || !NAME.test(token))
      return `"${token}" is not a value name in "{${source}}"`;
    terms.push({ sign, name: token });
    expectName = false;
  }
  if (terms.length === 0 || expectName)
    return `Empty or incomplete placeholder "{${source}}"`;
  return terms;
}

const PLURAL =
  /^(.*?),\s*plural\s*,\s*one\s*\{([^{}]*)\}\s*other\s*\{([^{}]*)\}\s*$/s;

function parsePlaceholder(source: string): TextPart | string {
  const plural = PLURAL.exec(source);
  if (plural) {
    const terms = parseTerms(plural[1] ?? "");
    if (typeof terms === "string") return terms;
    return {
      kind: "plural",
      terms,
      one: plural[2] ?? "",
      other: plural[3] ?? "",
    };
  }
  if (source.includes(","))
    return `Unknown placeholder form "{${source}}": use {value} or {value, plural, one {…} other {…}}`;
  const terms = parseTerms(source);
  return typeof terms === "string" ? terms : { kind: "value", terms };
}

/** Finds the `}` closing the placeholder opened at `start`, allowing one level of plural branches. */
function closingBrace(text: string, start: number): number {
  let depth = 0;
  for (let i = start; i < text.length; i++) {
    const char = text[i];
    if (char === "{") depth++;
    if (char === "}") {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

export function parseTextTemplate(text: string): TextTemplateParse {
  const template: TextTemplate = [];
  let index = 0;
  while (index < text.length) {
    const open = text.indexOf("{", index);
    if (open === -1) {
      template.push({ kind: "text", text: text.slice(index) });
      break;
    }
    if (open > index)
      template.push({ kind: "text", text: text.slice(index, open) });
    const close = closingBrace(text, open);
    if (close === -1)
      return { ok: false, error: `Unclosed "{" at character ${open + 1}` };
    const part = parsePlaceholder(text.slice(open + 1, close));
    if (typeof part === "string") return { ok: false, error: part };
    template.push(part);
    index = close + 1;
  }
  return { ok: true, template };
}

/** Every value name a template reads. */
export function textTemplateNames(template: TextTemplate): string[] {
  return template.flatMap((part) =>
    part.kind === "text" ? [] : part.terms.map((term) => term.name),
  );
}

/** A value a template can read: a number, or null for "nothing to show". */
export type TextValue = number | null;

/**
 * The terms' sum, or null when any of them is missing or null. Money arrives
 * as a decimal string and is read as a number here, for display only.
 */
export function evaluateTerms(
  terms: readonly TextTerm[],
  read: (name: string) => unknown,
): TextValue {
  let total = 0;
  for (const term of terms) {
    const raw = read(term.name);
    const value =
      typeof raw === "number"
        ? raw
        : typeof raw === "string" && raw.trim() !== ""
          ? Number(raw)
          : Number.NaN;
    if (!Number.isFinite(value)) return null;
    total += term.sign * value;
  }
  return total;
}

/**
 * Text that may hold `{placeholders}`: refused when one is malformed, with
 * the parser's reason.
 */
export function reportTemplateTextSchema(max: number) {
  return z
    .string()
    .max(max)
    .superRefine((text, ctx) => {
      const parsed = parseTextTemplate(text);
      if (!parsed.ok) ctx.addIssue({ code: "custom", message: parsed.error });
    });
}
